/**
 * HierarchySelect Component
 *
 * Reusable MUI Select wrapper for option lists that include hierarchical
 * groupings. Renders one non-selectable group header per hierarchy family
 * (with the supplied icon and label) followed by indented, tree-connected
 * MenuItems for each child option. Plain (non-grouped) options render as
 * standalone MenuItems with no indent and no icon.
 *
 * Used by Master Data Manager's field selector; suitable for any dropdown
 * whose options carry optional `groupKey` / `groupLabel` / `level` metadata.
 */

import React, { ReactNode } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

/** Single option in a HierarchySelect. */
export interface HierarchySelectOption {
  /** Unique value emitted via onChange when this row is picked. */
  value: string;
  /** User-visible label for the row. */
  label: string;
  /**
   * Optional grouping key. Consecutive options sharing the same groupKey
   * form a hierarchy family and render under one group header.
   */
  groupKey?: string;
  /** Header text for the group. Defaults to groupKey when omitted. */
  groupLabel?: string;
  /**
   * Depth within the family, 0-indexed. Used for indentation only.
   * Top-level children of a group use level 0.
   */
  level?: number;
}

export interface HierarchySelectProps {
  /** Currently selected option value. */
  value: string;
  /** Change handler — receives the standard MUI SelectChangeEvent. */
  onChange: (event: SelectChangeEvent) => void;
  /** Option list. Group children must appear consecutively to be grouped. */
  options: HierarchySelectOption[];
  /** Floating input label. */
  label?: string;
  /** Icon shown once per group header (e.g. <AccountTree />). */
  groupIcon?: ReactNode;
  /** Form size — passed through to MUI FormControl. */
  size?: 'small' | 'medium';
  /** sx for the FormControl. */
  sx?: SxProps<Theme>;
  /** Disabled state. */
  disabled?: boolean;
  /** Optional test ID. The Select gets `${dataTestId}-select`. */
  dataTestId?: string;
}

/** Pixels of left indent applied per hierarchy level. */
const INDENT_PER_LEVEL = 16;
/** Pixels reserved at left for the connector glyph / icon gutter. */
const CONNECTOR_GUTTER = 24;

const HierarchySelect: React.FC<HierarchySelectProps> = ({
  value,
  onChange,
  options,
  label,
  groupIcon,
  size = 'small',
  sx,
  disabled,
  dataTestId,
}) => {
  // Build a lookup so renderValue can show the canonical "Family - Label" string
  // for grouped options without having to inspect rendered MenuItem children.
  const optionsByValue = React.useMemo(() => {
    const map = new Map<string, HierarchySelectOption>();
    options.forEach((opt) => map.set(opt.value, opt));
    return map;
  }, [options]);

  const renderValue = (selected: unknown): ReactNode => {
    const opt = optionsByValue.get(selected as string);
    if (!opt) return '';
    const display =
      opt.groupLabel || opt.groupKey
        ? `${opt.groupLabel ?? opt.groupKey} - ${opt.label}`
        : opt.label;
    return (
      <Typography variant="body2" component="span">
        {display}
      </Typography>
    );
  };

  // Walk options once, emitting a ListSubheader whenever the groupKey changes.
  // ListSubheader/MenuItem pairs are sent to <Select> as direct children so
  // MUI's selection logic continues to work.
  const children: ReactNode[] = [];
  let lastGroupKey: string | undefined;

  options.forEach((opt, index) => {
    const isGrouped = Boolean(opt.groupKey);
    if (isGrouped && opt.groupKey !== lastGroupKey) {
      children.push(
        <ListSubheader
          key={`group-${opt.groupKey}-${index}`}
          data-testid={dataTestId ? `${dataTestId}-group-${opt.groupKey}` : undefined}
          disableSticky
          sx={{
            display: 'flex',
            alignItems: 'center',
            // Override MUI's default 16px left padding so the icon slot controls
            // horizontal alignment — the icon box is CONNECTOR_GUTTER wide, making
            // the header label start at the same x as flat MenuItem labels.
            pl: 0,
            lineHeight: '32px',
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          {/* Fixed-width icon slot so the header label aligns with flat option labels */}
          <Box
            component="span"
            sx={{
              width: `${CONNECTOR_GUTTER}px`,
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
            }}
          >
            {groupIcon}
          </Box>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            {opt.groupLabel ?? opt.groupKey}
          </Typography>
        </ListSubheader>
      );
      lastGroupKey = opt.groupKey;
    }
    if (!isGrouped) {
      lastGroupKey = undefined;
    }

    const level = isGrouped ? (opt.level ?? 0) : 0;
    // Flat rows start at CONNECTOR_GUTTER, matching the header label x position.
    // Grouped rows add (level + 1) * INDENT so each child sits to the right of
    // the header label, not alongside it or to its left.
    const indentPx = isGrouped
      ? CONNECTOR_GUTTER + (level + 1) * INDENT_PER_LEVEL
      : CONNECTOR_GUTTER;

    children.push(
      <MenuItem
        key={`option-${opt.value}-${index}`}
        value={opt.value}
        dense={isGrouped}
        data-testid={dataTestId ? `${dataTestId}-option-${opt.value}` : undefined}
        data-level={isGrouped ? level : undefined}
        style={{ paddingLeft: `${indentPx}px` }}
        sx={{
          position: 'relative',
          // CSS connector: a small L (vertical + horizontal) drawn to the
          // left of the label. Anchored to the indent boundary so labels
          // stay column-aligned across siblings and across the group header.
          ...(isGrouped && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: `${indentPx - 14}px`,
              top: 0,
              bottom: '50%',
              width: '10px',
              borderLeft: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider',
              borderBottomLeftRadius: '2px',
            },
          }),
        }}
      >
        <Typography variant="body2">{opt.label}</Typography>
      </MenuItem>
    );
  });

  return (
    <FormControl size={size} sx={sx} data-testid={dataTestId} disabled={disabled}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        value={value}
        onChange={onChange}
        label={label}
        renderValue={renderValue}
        data-testid={dataTestId ? `${dataTestId}-select` : undefined}
      >
        {children}
      </Select>
    </FormControl>
  );
};

export default HierarchySelect;
